import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { formatShoppingItem } from '../../utils/shoppingListHelpers';
import ShoppingListShareModal from './ShoppingListShareModal';
import { FaShareAlt, FaUsers } from 'react-icons/fa';
import '../../styles/recipes/QuickShoppingList.css';

const DEFAULT_LIST_STORAGE_KEY = 'quickTodoListDefaultId';
const SECONDARY_LIST_STORAGE_KEY = 'quickTodoListSecondaryId';

/**
 * Name einer frisch angelegten, leeren Standard-To-Do-Liste (kein Datum -
 * anders als bei "Heute Einkaufen" ist eine To-Do-Liste kein täglich neu
 * beginnendes Konzept).
 */
const generateListName = () => "My Todo's";

/**
 * Prüft ob eine Liste vollständig abgehakt ist (100% erledigt)
 */
const isListComplete = (list) => {
  if (!list || !list.items || list.items.length === 0) {
    return false; // Leere Liste gilt nicht als vollständig
  }
  const checkedItems = list.checkedItems instanceof Set
    ? list.checkedItems
    : new Set(list.checkedItems || []);
  return list.items.every((_, index) => checkedItems.has(index));
};

/**
 * Eine Liste gilt als To-Do, wenn sie explizit als 'TODO' getaggt ist ODER
 * (noch) gar keinen listType hat - siehe TODO_ShoppingListType_Backend.md:
 * ungetaggte Bestandslisten (aus der Zeit vor dieser Unterscheidung) gelten
 * bewusst als To-Do, nicht als Einkaufsliste.
 */
const isTodoList = (list) => !list.listType || list.listType === 'TODO';

/**
 * QuickTodoList - Minimale Schnell-To-Do-Ansicht
 *
 * Gleiches Layout/CSS wie QuickShoppingList.js (gleiche Teilen- und
 * Zwei-Slot-Swipe-Funktion), aber für To-Do-Listen statt Einkaufslisten.
 *
 * Anders als QuickShoppingList.js sucht diese Komponente bewusst NICHT
 * heuristisch nach irgendeiner "passenden" bestehenden Liste (weder über
 * geteilte/Collaborator-Listen noch über eine generische "erste
 * unvollständige Liste"-Suche) - das würde sonst versehentlich die echte
 * "Heute Einkaufen"-Einkaufsliste anzeigen, sobald diese (noch) ungetaggt
 * und aktiv geteilt ist. Stattdessen wird ausschließlich die selbst
 * gemerkte Standard-Listen-ID verwendet; existiert keine (oder ist die
 * gemerkte Liste 100% abgehakt), wird eine frische, leere Standardliste
 * "My Todo's" angelegt. Eine zweite Liste für die Swipe-Funktion muss
 * explizit über die Listen-Übersicht angelegt/gewählt werden (siehe
 * handleCreateFromPicker/handleSwitchToList).
 */
const QuickTodoList = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const {
    shoppingLists,
    shoppingListsLoading,
    fetchShoppingLists,
    createManualShoppingList,
    deleteShoppingList,
    addItemToList,
    toggleItemChecked,
    removeItemFromList
  } = useRecipe();

  const [defaultListId, setDefaultListId] = useState(null);
  const [secondaryListId, setSecondaryListId] = useState(() => {
    const stored = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
    return stored || null;
  });
  const [activeSlot, setActiveSlot] = useState(0); // 0 = primary (shared), 1 = secondary (private)
  const [newItemName, setNewItemName] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const minSwipeDistance = 50;

  const activeListId = activeSlot === 0 ? defaultListId : secondaryListId;

  const secondaryList = secondaryListId
    ? shoppingLists.find(list => list.id === secondaryListId || String(list.id) === String(secondaryListId))
    : null;

  const hasTwoLists = Boolean(defaultListId && secondaryListId);

  const activeList = activeListId
    ? shoppingLists.find(list => list.id === activeListId || String(list.id) === String(activeListId))
    : null;

  // Alle To-Do-Listen des Nutzers (für den "Andere Liste öffnen"-Umschalter).
  // Rein clientseitig aus den ohnehin schon geladenen Daten gefiltert - kein
  // zusätzlicher Request nötig. Wichtig, damit alte/weitere Listen (die durch
  // die Migration jetzt standardmäßig als TODO gelten) weiterhin erreichbar
  // bleiben, obwohl es keine "Alle Listen"-Übersicht mehr gibt.
  const allTodoLists = shoppingLists
    .filter(isTodoList)
    .filter(l => !String(l.id).startsWith('temp-'))
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

  // Frisch angelegte Listen bekommen zunächst eine temporäre "temp-..."-ID
  // (optimistic update) und werden erst asynchron mit der echten Backend-ID
  // synchronisiert (siehe RecipeContext.js). Sobald das passiert, aktualisiert
  // RecipeContext.js zwar schon localStorage - aber der eigene React-State
  // hier (defaultListId/secondaryListId) muss das noch nachziehen, sonst
  // "verschwindet" die gerade erst angelegte Liste aus der Ansicht.
  useEffect(() => {
    if (defaultListId && String(defaultListId).startsWith('temp-') &&
        !shoppingLists.some(l => String(l.id) === String(defaultListId))) {
      const stored = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY);
      if (stored && stored !== defaultListId) setDefaultListId(stored);
    }
    if (secondaryListId && String(secondaryListId).startsWith('temp-') &&
        !shoppingLists.some(l => String(l.id) === String(secondaryListId))) {
      const stored = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
      if (stored && stored !== secondaryListId) setSecondaryListId(stored);
    }
  }, [shoppingLists, defaultListId, secondaryListId]);

  // Gespeicherte Secondary-Liste validieren (nur "echte" IDs - eine gerade
  // erst angelegte "temp-..."-ID wird bewusst NICHT hier geprüft, sondern
  // ausschließlich vom Reconciliation-Effect oben behandelt. Würde man
  // "temp-..."-IDs hier auch prüfen, entstünde ein Race: setShoppingLists()
  // (Optimistic-Update, direkt in createManualShoppingList) und der
  // anschließende localStorage.setItem()/setSecondaryListId() in
  // handleCreateFromPicker sind durch awaits getrennt und lösen separate
  // Renderzyklen aus - dieser Effect könnte dann mit einer bereits wieder
  // veralteten shoppingLists-Momentaufnahme laufen und die frisch angelegte
  // temp-Liste fälschlich als "nicht gefunden" wieder entfernen.
  useEffect(() => {
    if (shoppingListsLoading || !shoppingLists.length) return;

    const storedSecondaryId = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
    if (!storedSecondaryId || storedSecondaryId.startsWith('temp-')) return;

    const secondaryExists = shoppingLists.find(
      list => (list.id === storedSecondaryId || String(list.id) === storedSecondaryId) && isTodoList(list)
    );
    if (secondaryExists) {
      setSecondaryListId(storedSecondaryId);
    } else {
      localStorage.removeItem(SECONDARY_LIST_STORAGE_KEY);
      setSecondaryListId(null);
    }
  }, [shoppingLists, shoppingListsLoading]);

  // Initialisierung: Standard-To-Do-Liste finden oder anlegen
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }

    if (shoppingListsLoading || hasInitializedRef.current) {
      return;
    }

    const initializeDefaultList = async () => {
      setIsInitializing(true);
      setError(null);
      hasInitializedRef.current = true;

      try {
        const storedPrimaryId = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY);

        if (storedPrimaryId) {
          const existingList = shoppingLists.find(
            list => list.id === storedPrimaryId || String(list.id) === storedPrimaryId
          );

          if (existingList) {
            if (isListComplete(existingList)) {
              // Abgehakte Standardliste - frische neue "My Todo's" anlegen
              const newListId = await createManualShoppingList(generateListName(), '', [], { listType: 'TODO' });
              localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(newListId));
              setDefaultListId(newListId);
            } else {
              setDefaultListId(storedPrimaryId);
            }
            setIsInitializing(false);
            return;
          }
        }

        // Keine gespeicherte/gültige Standard-Liste - bewusst KEINE Suche nach
        // bestehenden Listen (siehe Klassenkommentar oben) - einfach frische
        // leere "My Todo's"-Liste anlegen.
        const newListId = await createManualShoppingList(generateListName(), '', [], { listType: 'TODO' });
        localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(newListId));
        setDefaultListId(newListId);

      } catch (err) {
        console.error('Error finding/creating default todo list:', err);
        setError(t('quickTodo.error') || 'Fehler beim Laden der To-Do-Liste');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeDefaultList();
  }, [isOpen, shoppingLists, shoppingListsLoading, createManualShoppingList, t]);

  // Kleiner Timeout, damit der Fokus nicht durch die Öffnen-Animation/das
  // Layout wieder verloren geht (v.a. auf Smartphones).
  useEffect(() => {
    if (isOpen && !isInitializing && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isInitializing]);

  const handleAddItem = async () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName || !activeList || !activeList.id) {
      if (!activeList?.id) {
        console.error('Cannot add item: No active list or invalid list ID');
      }
      return;
    }

    const isDuplicate = activeList.items?.some(
      item => item.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setError(t('quickTodo.duplicateItem') || `"${trimmedName}" ist bereits in der Liste`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await addItemToList(activeList.id, {
        name: trimmedName,
        quantity: 0,
        unit: '',
        note: ''
      });
      setNewItemName('');
      setError(null);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error adding item:', err);
      const errorMsg = err?.response?.data?.message || err?.message || '';
      if (errorMsg.includes('existiert bereits') || errorMsg.includes('duplicate')) {
        setError(t('quickTodo.duplicateItem') || `"${trimmedName}" ist bereits in der Liste`);
      } else {
        setError(t('quickTodo.addError') || 'Fehler beim Hinzufügen');
      }
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleToggleItem = async (itemIndex) => {
    if (!activeList || !activeList.id) {
      console.error('Cannot toggle item: No active list or invalid list ID');
      return;
    }
    if (itemIndex === undefined || itemIndex === null || itemIndex < 0) {
      console.error('Cannot toggle item: Invalid item index', itemIndex);
      return;
    }
    try {
      await toggleItemChecked(activeList.id, itemIndex);
    } catch (err) {
      console.error('Error toggling item:', err);
    }
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    if (!hasTwoLists) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeSlot === 0) {
      setActiveSlot(1);
    } else if (isRightSwipe && activeSlot === 1) {
      setActiveSlot(0);
    }
  };

  const onMouseDown = (e) => {
    if (e.target.closest('button, input, a, .quick-list-item')) return;
    setIsDragging(true);
    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;
    if (!hasTwoLists) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeSlot === 0) {
      setActiveSlot(1);
    } else if (isRightSwipe && activeSlot === 1) {
      setActiveSlot(0);
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setShowOverflowMenu(false);
    try {
      await fetchShoppingLists();
    } catch (err) {
      console.error('Error refreshing todo lists:', err);
      setError(t('quickTodo.error') || 'Fehler beim Aktualisieren');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Wechselt die im aktiven Slot angezeigte Liste auf eine andere,
   * ausgewählte To-Do-Liste (z.B. eine alte Bestandsliste, die sonst nicht
   * mehr erreichbar wäre, da es keine "Alle Listen"-Übersicht mehr gibt).
   */
  const handleSwitchToList = (listId) => {
    setShowListPicker(false);
    setShowOverflowMenu(false);
    if (activeSlot === 1) {
      localStorage.setItem(SECONDARY_LIST_STORAGE_KEY, String(listId));
      setSecondaryListId(listId);
    } else {
      localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(listId));
      setDefaultListId(listId);
    }
  };

  /**
   * Legt aus der Listen-Übersicht heraus eine neue leere "My Todo's"-Liste an
   * und setzt sie direkt als die im aktiven Slot (Haupt- oder Zweitliste)
   * angezeigte Liste.
   */
  const handleCreateFromPicker = async () => {
    try {
      const newListId = await createManualShoppingList(generateListName(), '', [], { listType: 'TODO' });
      handleSwitchToList(newListId);
    } catch (err) {
      console.error('Error creating todo list:', err);
      setError(t('quickTodo.addSecondaryError') || 'Fehler beim Anlegen der Liste');
      setTimeout(() => setError(null), 3000);
    }
  };

  /**
   * Löscht eine To-Do-Liste endgültig aus der Listen-Übersicht heraus. Zeigt
   * die gelöschte Liste gerade im Haupt- oder Zweit-Slot an, wird der
   * jeweilige Zeiger zurückgesetzt (die Init-Logik legt beim nächsten Öffnen
   * automatisch wieder eine frische Standardliste an).
   */
  const handleDeleteList = async (listId) => {
    if (!window.confirm(t('quickTodo.confirmDeleteList') || 'Diese Liste wirklich endgültig löschen?')) {
      return;
    }
    try {
      await deleteShoppingList(listId);
      if (String(defaultListId) === String(listId)) {
        localStorage.removeItem(DEFAULT_LIST_STORAGE_KEY);
        setDefaultListId(null);
        hasInitializedRef.current = false;
      }
      if (String(secondaryListId) === String(listId)) {
        localStorage.removeItem(SECONDARY_LIST_STORAGE_KEY);
        setSecondaryListId(null);
        setActiveSlot(0);
      }
    } catch (err) {
      console.error('Error deleting todo list:', err);
      setError(t('quickTodo.deleteListError') || 'Fehler beim Löschen der Liste');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleRemoveItem = async (itemIndex) => {
    if (!activeList || !activeList.id) {
      console.error('Cannot remove item: No active list or invalid list ID');
      return;
    }
    if (itemIndex === undefined || itemIndex === null || itemIndex < 0) {
      console.error('Cannot remove item: Invalid item index', itemIndex);
      return;
    }
    try {
      await removeItemFromList(activeList.id, itemIndex);
    } catch (err) {
      console.error('Error removing item:', err);
      setError(t('quickTodo.removeError') || 'Fehler beim Entfernen');
      setTimeout(() => setError(null), 3000);
    }
  };

  const totalItems = activeList?.items?.length || 0;
  const checkedCount = activeList?.checkedItems?.size || 0;
  const progressText = t('quickTodo.progress')
    ?.replace('{completed}', checkedCount)
    ?.replace('{total}', totalItems) || `${checkedCount} von ${totalItems} erledigt`;

  const uncheckedItems = activeList?.items?.filter((_, index) => {
    const isChecked = activeList.checkedItems?.has?.(index) ||
      (Array.isArray(activeList.checkedItems) && activeList.checkedItems.includes(index));
    return !isChecked;
  }) || [];

  const checkedItems = activeList?.items?.filter((_, index) => {
    const isChecked = activeList.checkedItems?.has?.(index) ||
      (Array.isArray(activeList.checkedItems) && activeList.checkedItems.includes(index));
    return isChecked;
  }) || [];

  const getOriginalIndex = (item) => {
    return activeList?.items?.findIndex(i => i === item) ?? -1;
  };

  if (!isOpen) return null;

  return (
    <div className="quick-shopping-list-overlay" onClick={onClose}>
      <div
        className={`quick-shopping-list-panel${isDragging ? ' dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        {!isInitializing && !shoppingListsLoading && activeList && (
          <div className="quick-list-add-section" data-testid="quick-todo-add-section">
            <input
              ref={inputRef}
              type="text"
              className="quick-list-input"
              placeholder={t('quickTodo.placeholder') || 'Aufgabe hinzufügen...'}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="quick-todo-input"
            />
            <button
              className="quick-list-add-btn"
              onClick={handleAddItem}
              disabled={!newItemName.trim()}
              data-testid="quick-todo-add-btn"
            >
              +
            </button>
          </div>
        )}

        {hasTwoLists && (
          <div className="quick-list-swipe-indicator">
            <button
              className={`swipe-dot ${activeSlot === 0 ? 'active' : ''}`}
              onClick={() => setActiveSlot(0)}
              title={t('quickTodo.sharedList') || 'Geteilte Liste'}
            />
            <button
              className={`swipe-dot ${activeSlot === 1 ? 'active' : ''}`}
              onClick={() => setActiveSlot(1)}
              title={t('quickTodo.privateList') || 'Private Liste'}
            />
          </div>
        )}

        {activeList && (
          <div className={`quick-list-info-banner ${activeSlot === 1 ? 'private' : 'shared'}`}>
            {activeSlot === 0 ? (
              <>
                <span className="banner-icon">{activeList.isShared || (activeList.collaborators && activeList.collaborators.length > 0) ? '👥' : '🔗'}</span>
                <span className="banner-text">
                  {activeList.name}
                  {(activeList.isShared || (activeList.collaborators && activeList.collaborators.length > 0)) ? (
                    <span className="banner-badge shared">{t('quickTodo.activelyShared') || '(Aktiv geteilt)'}</span>
                  ) : (
                    <span className="banner-badge">{t('quickTodo.primaryList') || '(Hauptliste)'}</span>
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="banner-icon">📝</span>
                <span className="banner-text">
                  {activeList.name}
                  <span className="banner-badge">{t('quickTodo.private') || '(Privat)'}</span>
                </span>
              </>
            )}
          </div>
        )}

        <div className="quick-list-header">
          <div className="quick-list-header-left">
            {hasTwoLists && (
              <div className="quick-list-nav-buttons">
                <button
                  className={`quick-list-nav-btn ${activeSlot === 0 ? 'disabled' : ''}`}
                  onClick={() => setActiveSlot(0)}
                  disabled={activeSlot === 0}
                  title={t('quickTodo.primaryList') || 'Hauptliste'}
                >
                  ‹
                </button>
                <button
                  className={`quick-list-nav-btn ${activeSlot === 1 ? 'disabled' : ''}`}
                  onClick={() => setActiveSlot(1)}
                  disabled={activeSlot === 1}
                  title={t('quickTodo.private') || 'Private Liste'}
                >
                  ›
                </button>
              </div>
            )}
          </div>
          <div className="header-center" />
          <div className="quick-list-header-actions">
            {activeList && (
              <button
                className="quick-list-share-btn"
                onClick={() => setShowShareModal(true)}
                title={t('share.shareList') || 'Liste teilen'}
              >
                <FaShareAlt />
              </button>
            )}
            <button
              className="quick-list-menu-btn"
              onClick={() => setShowOverflowMenu(!showOverflowMenu)}
              title={t('menu') || 'Menü'}
            >
              ⋮
            </button>
            {showOverflowMenu && (
              <>
                <div className="overflow-backdrop" onClick={() => setShowOverflowMenu(false)} />
                <div className="overflow-dropdown">
                  <button
                    className="overflow-item"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? '⟳' : '↻'} {t('quickTodo.refresh') || 'Aktualisieren'}
                  </button>
                  <button
                    className="overflow-item"
                    onClick={() => {
                      setShowOverflowMenu(false);
                      setShowListPicker(true);
                    }}
                  >
                    📋 {t('quickTodo.switchList') || 'Listen verwalten'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Listen-Übersicht: alle To-Do-Listen des Nutzers - hier lässt sich
            eine bestehende Liste auswählen, eine neue anlegen oder eine
            bestehende endgültig löschen (siehe allTodoLists oben) */}
        {showListPicker && (
          <>
            <div className="overflow-backdrop" onClick={() => setShowListPicker(false)} />
            <div className="quick-list-list-picker">
              <div className="list-picker-header">
                <h3>{t('quickTodo.switchListTitle') || 'To-Do-Liste wählen'}</h3>
                <button className="list-picker-close" onClick={() => setShowListPicker(false)}>✕</button>
              </div>
              {/* Ziel-Auswahl: legt fest, ob "Neue Liste anlegen" bzw. eine
                  ausgewählte Liste als Haupt- oder Zweitliste (Swipe-Slot)
                  verwendet wird - unabhängig davon, ob schon eine Zweitliste
                  existiert (sonst gäbe es keine Möglichkeit, überhaupt die
                  erste Zweitliste anzulegen). */}
              <div className="list-picker-slot-selector">
                <button
                  className={activeSlot === 0 ? 'active' : ''}
                  onClick={() => setActiveSlot(0)}
                >
                  {t('quickTodo.slotPrimary') || 'Hauptliste'}
                </button>
                <button
                  className={activeSlot === 1 ? 'active' : ''}
                  onClick={() => setActiveSlot(1)}
                >
                  {t('quickTodo.slotSecondary') || 'Zweite Liste'}
                </button>
              </div>
              <div className="list-picker-items">
                <button className="list-picker-item list-picker-add" onClick={handleCreateFromPicker}>
                  <span className="list-picker-item-name">➕ {t('quickTodo.newList') || 'Neue Liste anlegen'}</span>
                </button>
                {allTodoLists.length === 0 ? (
                  <p className="list-picker-empty">{t('quickTodo.noOtherLists') || 'Keine weiteren Listen vorhanden'}</p>
                ) : (
                  allTodoLists.map(list => {
                    const total = list.items?.length || 0;
                    const checked = list.checkedItems?.size ?? (Array.isArray(list.checkedItems) ? list.checkedItems.length : 0);
                    const isCurrent = String(list.id) === String(activeListId);
                    return (
                      <div key={list.id} className={`list-picker-row ${isCurrent ? 'current' : ''}`}>
                        <button
                          className="list-picker-item"
                          onClick={() => handleSwitchToList(list.id)}
                        >
                          <span className="list-picker-item-name">{list.name}</span>
                          <span className="list-picker-item-meta">
                            {isCurrent && '✓ '}{checked}/{total}
                          </span>
                        </button>
                        <button
                          className="list-picker-delete"
                          onClick={() => handleDeleteList(list.id)}
                          title={t('delete') || 'Löschen'}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}

        <div className="quick-list-content">
          {(isInitializing || shoppingListsLoading) && (
            <div className="quick-list-loading">
              <div className="loading-spinner"></div>
              <p>{t('loading') || 'Lädt...'}</p>
            </div>
          )}

          {error && (
            <div className="quick-list-error">
              {error}
            </div>
          )}

          {!isInitializing && !shoppingListsLoading && activeList && (
            <>
              <div className="quick-list-items" data-testid="quick-todo-items">
                {activeList.items.length === 0 ? (
                  <div className="quick-list-empty">
                    <p>{t('quickTodo.empty') || 'Noch keine Aufgaben'}</p>
                    <p className="quick-list-empty-hint">
                      {t('quickTodo.emptyHint') || 'Füge deine erste Aufgabe hinzu!'}
                    </p>
                  </div>
                ) : (
                  <>
                    <ul className="quick-list-item-list">
                      {uncheckedItems.map((item) => {
                        const originalIndex = getOriginalIndex(item);
                        return (
                          <li
                            key={originalIndex}
                            className="quick-list-item"
                            data-testid="quick-todo-item"
                          >
                            <button
                              className="quick-list-checkbox-btn"
                              onClick={() => handleToggleItem(originalIndex)}
                              aria-label="Mark as done"
                            >
                              <span className="checkbox-circle"></span>
                            </button>
                            <span className="quick-list-item-text">
                              {formatShoppingItem(item)}
                            </span>
                            <button
                              className="quick-list-item-delete"
                              onClick={() => handleRemoveItem(originalIndex)}
                              title={t('delete') || 'Löschen'}
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {totalItems > 0 && (
                      <div className="quick-list-progress-divider">
                        <span className="quick-list-progress-text">{progressText}</span>
                      </div>
                    )}

                    {checkedItems.length > 0 && (
                      <ul className="quick-list-item-list checked-items">
                        {checkedItems.map((item) => {
                          const originalIndex = getOriginalIndex(item);
                          return (
                            <li
                              key={originalIndex}
                              className="quick-list-item checked"
                              data-testid="quick-todo-item"
                            >
                              <button
                                className="quick-list-checkbox-btn checked"
                                onClick={() => handleToggleItem(originalIndex)}
                                aria-label="Unmark"
                              >
                                <span className="checkbox-circle">✓</span>
                              </button>
                              <span className="quick-list-item-text strikethrough">
                                {formatShoppingItem(item)}
                              </span>
                              <button
                                className="quick-list-item-delete"
                                onClick={() => handleRemoveItem(originalIndex)}
                                title={t('delete') || 'Löschen'}
                              >
                                ×
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <ShoppingListShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          listId={activeList?.id}
          listName={activeList?.name}
        />
      </div>
    </div>
  );
};

export default QuickTodoList;
